import React, { useState, useEffect, useMemo } from 'react'
import DatePicker from 'react-datepicker'
import { Button } from 'react-bootstrap'

import 'react-datepicker/dist/react-datepicker.css'
import './InputDateRange.scss'

/* =======================
   Tipos
======================= */

interface DateRangeOutput {
  startsAt: string
  endsAt: string
}

interface DateRangeInputProps {
  /** Valores iniciales (YYYY-MM-DD) */
  startsAt?: string
  endsAt?: string

  /** Rango válido del proyecto (YYYY-MM-DD) */
  projectStartDate: string
  projectEndDate: string

  /** Emite SOLO cuando el usuario presiona "Continuar" */
  onChange: (range: DateRangeOutput) => void

  /** Opcional */
  onCancel?: () => void

  /** Fechas no laborables (YYYY-MM-DD) que no pueden ser elegidas */
  nonWorkingDays?: string[]
}

/* =======================
   Helpers
======================= */

const parseDate = (value?: string): Date | null => {
  if (!value) return null
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d)
}

const formatDate = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/* =======================
   Componente
======================= */

const InputDateRange: React.FC<DateRangeInputProps> = ({
  startsAt,
  endsAt,
  projectStartDate,
  projectEndDate,
  onChange,
  onCancel,
  nonWorkingDays = [],
}) => {
  const [draftStart, setDraftStart] = useState<Date | null>(
    parseDate(startsAt)
  )
  const [draftEnd, setDraftEnd] = useState<Date | null>(
    parseDate(endsAt)
  )

  // Responsive months: 1 on mobile, 2 on lg, 3 on xl (clamped to project range)
  const [monthsShown, setMonthsShown] = useState<number>(() => {
    if (typeof window === 'undefined') return 1
    if (window.matchMedia('(min-width: 1200px)').matches) return 2
    if (window.matchMedia('(min-width: 992px)').matches) return 2
    return 1
  })

  const monthDiff = (a: Date, b: Date) =>
    (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1

  // Parse project range (props are YYYY-MM-DD strings)
  const projStartDate = parseDate(projectStartDate) ?? new Date()
  const projEndDate = parseDate(projectEndDate) ?? new Date()

  // Set para chequeos rápidos de fechas no laborables (formato YYYY-MM-DD)
  const nonWorkingSet = useMemo(() => new Set(nonWorkingDays || []), [nonWorkingDays])

  const totalMonths = Math.max(1, monthDiff(projStartDate, projEndDate))
  const visibleMonths = Math.min(monthsShown, totalMonths)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mqXl = window.matchMedia('(min-width: 1200px)')
    const mqLg = window.matchMedia('(min-width: 992px)')

    const update = () => {
      if (mqXl.matches) setMonthsShown(3)
      else if (mqLg.matches) setMonthsShown(2)
      else setMonthsShown(1)
    }

    update()
    const add = (mq: MediaQueryList, fn: () => void) =>
      mq.addEventListener ? mq.addEventListener('change', fn) : mq.addListener(fn)
    const remove = (mq: MediaQueryList, fn: () => void) =>
      mq.removeEventListener ? mq.removeEventListener('change', fn) : mq.removeListener(fn)

    add(mqXl, update)
    add(mqLg, update)

    return () => {
      remove(mqXl, update)
      remove(mqLg, update)
    }
  }, [projectStartDate, projectEndDate])

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setDraftStart(start)
    setDraftEnd(end)
  }

  const handleCancel = () => {
    setDraftStart(parseDate(startsAt))
    setDraftEnd(parseDate(endsAt))
    onCancel?.()
  }

  const handleContinue = () => {
    if (!draftStart || !draftEnd) return

    onChange({
      startsAt: formatDate(draftStart),
      endsAt: formatDate(draftEnd),
    })
  }

  return (
    <div>
      <DatePicker
        inline
        selectsRange
        filterDate={(date) => !nonWorkingSet.has(formatDate(date))}
        monthsShown={visibleMonths}
        wrapperClassName="input-date-range__wrapper"
        calendarClassName="input-date-range__calendar"
        className="input-date-range__root"
        startDate={draftStart}
        endDate={draftEnd}
        minDate={projStartDate}
        maxDate={projEndDate}
        openToDate={draftStart ?? projStartDate}
        onChange={handleDateChange}
        renderCustomHeader={({
          monthDate,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => {
          const prevDisabled =
            prevMonthButtonDisabled ||
            monthDate <= projStartDate

          const nextDisabled =
            nextMonthButtonDisabled ||
            monthDate >= projEndDate

          return (
            <div className="d-flex justify-content-between align-items-center px-2">
              <Button
                variant="light"
                size="sm"
                onClick={decreaseMonth}
                disabled={prevDisabled}
              >
                ‹
              </Button>

              <span className="font-weight-bold">
                {monthDate.toLocaleString('es-PE', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>

              <Button
                variant="light"
                size="sm"
                onClick={increaseMonth}
                disabled={nextDisabled}
              >
                ›
              </Button>
            </div>
          )
        }}
      />

      <div className="d-flex justify-content-end mt-3">
        <Button
          variant="outline-secondary"
          size="sm"
          className="mr-2"
          onClick={handleCancel}
        >
          Cancelar
        </Button>

        <Button
          variant="primary"
          size="sm"
          onClick={handleContinue}
          disabled={!draftStart || !draftEnd}
        >
          Continuar
        </Button>
      </div>
    </div>
  )
}

export default InputDateRange